import { useState } from "react";

import { useNavigate } from "react-router-dom";

import dayjs from "dayjs";
import {
  ArrowLeft,
  ArrowRightLeft,
  Filter,
  Plus,
  RefreshCw,
} from "lucide-react";

import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { useMaquilaMovements } from "~/hooks/maquila";
import {
  MAQUILA_MOVEMENT_TYPE_COLORS,
  MAQUILA_MOVEMENT_TYPE_LABELS,
  type MaquilaMovementFilters,
  type MaquilaMovementType,
} from "~/types/maquila";

export const MaquilaMovementsPage = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<MaquilaMovementFilters>({});
  const [showFilters, setShowFilters] = useState(false);

  const { data: movements, isLoading, refetch } = useMaquilaMovements(filters);

  const updateFilter = (key: keyof MaquilaMovementFilters, value: string) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value === "all" ? undefined : value,
    }));
  };

  return (
    <div className="mx-auto w-full max-w-6xl p-6 lg:w-[80vw]">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/maquila")}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-xl font-bold text-gray-900">
              Historial de Movimientos
            </h1>
            <p className="text-sm text-muted-foreground">
              Todos los movimientos de materiales maquila
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter className="mr-2 h-4 w-4" />
            Filtros
          </Button>
          <Button variant="outline" size="icon" onClick={() => refetch()}>
            <RefreshCw className="h-4 w-4" />
          </Button>
          <Button onClick={() => navigate("/maquila/movements/new")}>
            <Plus className="mr-2 h-4 w-4" />
            Nuevo Movimiento
          </Button>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <Card className="mb-6">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">Filtros</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div>
                <label className="mb-1 block text-xs text-muted-foreground">
                  Tipo de Movimiento
                </label>
                <Select
                  value={filters.movement_type || "all"}
                  onValueChange={(v) => updateFilter("movement_type", v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="entry">Entrada</SelectItem>
                    <SelectItem value="exit">Salida</SelectItem>
                    <SelectItem value="transfer">Transferencia</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="mb-1 block text-xs text-muted-foreground">
                  Desde
                </label>
                <Input
                  type="date"
                  value={filters.date_from || ""}
                  onChange={(e) => updateFilter("date_from", e.target.value)}
                />
              </div>
              <div>
                <label className="mb-1 block text-xs text-muted-foreground">
                  Hasta
                </label>
                <Input
                  type="date"
                  value={filters.date_to || ""}
                  onChange={(e) => updateFilter("date_to", e.target.value)}
                />
              </div>
              <div className="flex items-end">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setFilters({})}
                  className="w-full"
                >
                  Limpiar filtros
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : movements?.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <ArrowRightLeft className="mb-2 h-8 w-8" />
              <p>No hay movimientos registrados</p>
              <Button
                variant="link"
                className="mt-2"
                onClick={() => navigate("/maquila/movements/new")}
              >
                Registrar primer movimiento
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-20">#</TableHead>
                  <TableHead>Fecha</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Material</TableHead>
                  <TableHead className="text-right">Cantidad</TableHead>
                  <TableHead>Origen</TableHead>
                  <TableHead>Destino</TableHead>
                  <TableHead>Referencia</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {movements?.map((movement, index) => (
                  <TableRow
                    key={movement.id}
                    style={{ animationDelay: `${Math.min(index * 40, 400)}ms` }}
                    className="duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both"
                  >
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {movement.movement_number}
                    </TableCell>
                    <TableCell>
                      {dayjs(movement.movement_date).format("DD/MM/YYYY HH:mm")}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="secondary"
                        className={
                          MAQUILA_MOVEMENT_TYPE_COLORS[
                            movement.movement_type as MaquilaMovementType
                          ]
                        }
                      >
                        {
                          MAQUILA_MOVEMENT_TYPE_LABELS[
                            movement.movement_type as MaquilaMovementType
                          ]
                        }
                      </Badge>
                    </TableCell>
                    <TableCell>{movement.client?.name}</TableCell>
                    <TableCell>
                      {movement.maquila_item?.name}
                      {movement.maquila_item?.code && (
                        <span className="ml-1 text-xs text-muted-foreground">
                          ({movement.maquila_item.code})
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {Number(movement.quantity).toLocaleString("es-CL")}{" "}
                      {movement.unit}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {movement.from_area?.name || "-"}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {movement.to_area?.name || "-"}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {movement.reference_document || "-"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
